"use client";

import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import { AnimatePresence, motion } from "framer-motion";

export default function LogoLoader() {
  const pathname = usePathname();
  const [isLoading, setIsLoading] = useState(true); // start true
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    setIsLoading(true);
    setProgress(0);

    let p = 0;
    const interval = setInterval(() => {
      p += 10;
      setProgress(p);
      if (p >= 100) {
        clearInterval(interval);
        setTimeout(() => setIsLoading(false), 300);
      }
    }, 50);

    return () => clearInterval(interval);
  }, [pathname]);

  return (
    <AnimatePresence>
      {isLoading && (
        <motion.div
          className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-white"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="w-48 h-48 relative overflow-hidden">
            {/* Gray Logo Base */}
            <svg
              viewBox="0 0 3024 3024"
              xmlns="http://www.w3.org/2000/svg"
              className="w-full h-full absolute"
            >
              <path
                d="M1511.77 0c834.93,0 1511.77,676.84 1511.77,1511.77 0,834.93 -676.84,1511.77 -1511.77,1511.77 -219.59,0 -428.24,-46.84 -616.49,-131.06l0.51 -1082.07c7.78,-22.01 -2.82,-8.78 13.27,-21.25 85.18,99.89 180.68,186.41 284.03,288.73 22.94,22.73 81.81,101.34 155.66,153.98 307.91,219.49 611.74,-344.76 905.18,-633.66 50.42,-49.66 217.28,-231.68 263.14,-252.31 50.28,37.23 87.04,102.73 146.03,129.01l0.11 -724.91 -729 0.89 130.45 143.8c-22.81,40.35 -249.32,260.43 -287.21,297.41 -99.34,96.91 -188.59,188.67 -287.51,286.7 -33.06,-15.1 -368.95,-361.06 -441.96,-434.15 -26.55,-26.57 -71.46,-76.15 -109.98,-111.17 -14.51,-13.15 -28.12,-24.28 -39.49,-31.34 -355.8,-90.64 -598,-37.57 -584.21,339.44l-3.12 1188.87c-195.14,-254.64 -311.19,-573.08 -311.19,-918.69 0,-834.93 676.84,-1511.77 1511.77,-1511.77z"
                fill="#d1d5db"
              />
            </svg>

            {/* Orange Fill with Animated Wave Clipping */}
            <svg
              viewBox="0 0 3024 3024"
              xmlns="http://www.w3.org/2000/svg"
              className="w-full h-full absolute"
            >
              <defs>
                <clipPath id="wave-clip">
                  <motion.path
                    initial={false}
                    animate={{
                      y: 3024 - (progress / 100) * 3024,
                      d: [
                        "M0 0 Q800 -100 1600 0 T3024 0 V3024 H0 Z",
                        "M0 20 Q800 -60 1600 20 T3024 20 V3024 H0 Z",
                        "M0 0 Q800 -100 1600 0 T3024 0 V3024 H0 Z",
                      ],
                    }}
                    transition={{
                      y: { duration: 0.3 },
                      d: { duration: 1.2, repeat: Infinity, ease: "easeInOut" },
                    }}
                  />
                </clipPath>
              </defs>

              <path
                d="M1511.77 0c834.93,0 1511.77,676.84 1511.77,1511.77 0,834.93 -676.84,1511.77 -1511.77,1511.77 -219.59,0 -428.24,-46.84 -616.49,-131.06l0.51 -1082.07c7.78,-22.01 -2.82,-8.78 13.27,-21.25 85.18,99.89 180.68,186.41 284.03,288.73 22.94,22.73 81.81,101.34 155.66,153.98 307.91,219.49 611.74,-344.76 905.18,-633.66 50.42,-49.66 217.28,-231.68 263.14,-252.31 50.28,37.23 87.04,102.73 146.03,129.01l0.11 -724.91 -729 0.89 130.45 143.8c-22.81,40.35 -249.32,260.43 -287.21,297.41 -99.34,96.91 -188.59,188.67 -287.51,286.7 -33.06,-15.1 -368.95,-361.06 -441.96,-434.15 -26.55,-26.57 -71.46,-76.15 -109.98,-111.17 -14.51,-13.15 -28.12,-24.28 -39.49,-31.34 -355.8,-90.64 -598,-37.57 -584.21,339.44l-3.12 1188.87c-195.14,-254.64 -311.19,-573.08 -311.19,-918.69 0,-834.93 676.84,-1511.77 1511.77,-1511.77z"
                fill="#FF7F32"
                clipPath="url(#wave-clip)"
              />
            </svg>

            {/* Constant wave splashes */}
            {[...Array(5)].map((_, i) => (
              <motion.div
                key={`wave-splash-${i}`}
                className="absolute bg-orange-300 rounded-full"
                style={{
                  width: `${Math.random() * 5 + 3}px`,
                  height: `${Math.random() * 5 + 3}px`,
                  left: `${Math.random() * 80 + 10}%`,
                  bottom: `${(progress / 100) * 48 + 20}px`,
                  zIndex: 15,
                }}
                initial={{ y: 0, opacity: 0.8 }}
                animate={{
                  y: [0, -20],
                  opacity: [0.8, 0],
                }}
                transition={{
                  duration: 1.2 + Math.random() * 0.5,
                  repeat: Infinity,
                  delay: i * 0.3,
                }}
              />
            ))}

          </div>

          {/* Splash on complete */}
          {progress === 100 && (
            <motion.div
              initial={{ scale: 0, opacity: 0.5 }}
              animate={{ scale: 1.6, opacity: 0 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              className="absolute w-48 h-48 rounded-full border-8 border-orange-400"
            />
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
